import { useEffect, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import { HEADER_DEFAULT_Y } from '@/features/shared/constants/global'

import { BREAKPOINTS } from '@shipt/design-system-themes'

interface DOMRectHeader {
  top: number
  width: number
  height: number
  x: number
  y: number
  update: () => DOMRect
}

export const STICKY_HEADER_ID = 'sticky-header'
export const MOBILE_STICKY_HEADER_ID = 'mobile-sticky-header'
export const MOBILE_BG_POSITIONER_ID = 'mobile-bg-positioner'
export const MOBILE_STICKY_NAV_ID = 'mobile-sticky-nav'
const HEADER_DEFAULT_HEIGHT = 56

// defaultRect only contains the properties we need from the type DOMrect
// therefore, the type is just Partial<DOMRect>
// oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
const defaultRect = {
  top: 0,
  width: 0,
  height: HEADER_DEFAULT_HEIGHT,
  x: 0,
  y: HEADER_DEFAULT_Y,
} as DOMRect

const defaultRectHeader = {
  ...defaultRect,
  update: () => defaultRect,
}

export const useHeaderRect = ({
  enabled = false,
  updateOnResize = false,
  updateOnScroll = false,
  visibleOnly = false,
} = {}) => {
  const [headerRect, setHeaderRect] = useState<DOMRectHeader>(defaultRectHeader)
  const _isMounted = useRef(false)
  useEffect(() => {
    _isMounted.current = true
    return () => {
      _isMounted.current = false
    }
  }, [])

  useEffect(() => {
    const getBoundingRect = () => {
      const headerEl = document.getElementById(STICKY_HEADER_ID)
      // If mobile, use the mobile sticky header if it exists, otherwise use the desktop header
      const rect = (
        window.innerWidth < BREAKPOINTS.MEDIUM
          ? document.getElementById(MOBILE_STICKY_HEADER_ID) || headerEl
          : headerEl
      )?.getBoundingClientRect()

      if (!rect) return defaultRect

      // When visibleOnly is set on mobile, subtract the decorative background
      // positioner height so consumers get the actual visible header height.
      if (visibleOnly && window.innerWidth < BREAKPOINTS.MEDIUM) {
        const positioner = document.getElementById(MOBILE_BG_POSITIONER_ID)
        const positionerHeight = positioner?.getBoundingClientRect().height ?? 0
        // The positioner has a fixed height that may exceed its visible content:
        // when the global category nav is rendered inside it, that nav is part of
        // the visible header, so add its height back so it isn't subtracted away.
        const nav = document.getElementById(MOBILE_STICKY_NAV_ID)
        const navHeight = nav?.getBoundingClientRect().height ?? 0
        return { ...rect, height: rect.height - positionerHeight + navHeight }
      }

      return rect
    }

    const debouncedListener = debounce(() => {
      const { top, width, height, x, y } = getBoundingRect()
      if (_isMounted.current) {
        setHeaderRect({
          top,
          width,
          height,
          x,
          y,
          update: getBoundingRect,
        })
      }
    }, 300)

    // Set the initial Header rect properties
    debouncedListener()

    if (!enabled) return

    if (updateOnResize) window.addEventListener('resize', debouncedListener)
    if (updateOnScroll) window.addEventListener('scroll', debouncedListener)

    return () => {
      window.removeEventListener('resize', debouncedListener)
      window.removeEventListener('scroll', debouncedListener)
    }
  }, [updateOnResize, updateOnScroll, enabled, visibleOnly])

  return headerRect
}
